import * as React from 'react';

import { getLocalTimeZone, today } from '@internationalized/date';
import {
  enumCurrencyCode,
  enumWeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { createLoadAtom } from '../../../_graphql/mutationLoadCreate';
import { $outboundLoads } from '../../../_state/scaleTicketPro.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { tradeRoutePaths as tradeRoutes } from '../../../routes';

import { useIdentity } from '~/shared/hooks';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

export const useCreateOutboundTicket = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const toast = useToast();
  const { facilityId } = useIdentity();

  const { refetch: refetchOutbound } = useAtomValue($outboundLoads);

  const [isCreating, setIsCreating] = React.useState(false);

  const createLoadMutation = useSetAtom(
    createLoadAtom({
      onSuccess: () => {
        refetchOutbound?.();
      },
    }),
  );

  const createTicket = React.useCallback(async () => {
    if (isCreating) return;

    if (!facilityId) {
      toast.error(t.scaleTicketPro.noFacilitiesAvailable);
      return;
    }

    setIsCreating(true);
    try {
      const result = await createLoadMutation({
        expectedShipDate: today(getLocalTimeZone()),
        facilityId,
        currencyCode: enumCurrencyCode.USD,
        weightDisplayUnit: enumWeightDisplayUnit.ScaleUnit,
      });

      if (result?.tradeLoadCreate?.id) {
        navigate(
          tradeRoutes.scaleTicketDetail.replace(
            ':id',
            result.tradeLoadCreate.id,
          ),
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to create ticket',
      );
    } finally {
      setIsCreating(false);
    }
  }, [isCreating, facilityId, createLoadMutation, navigate, toast, t]);

  return { createTicket, isCreating };
};
